// ** React imports
import React, { useMemo } from 'react';

// ** Third Party imports
import { Button } from '@saleshandy/design-system';
import Modal from '../../../../design-system/components/atoms/modal';

// ** Utilities & Enums
import { detectBrowser } from '../../../../utils/detect-browser';
import { Browser } from '../../../../enums/browsers';

// ─── Alert Circle Icon ────────────────────────────────────────────────────────

interface AlertCircleIconProps {
  width?: number;
  height?: number;
}
export const AlertCircleIcon: React.FC<AlertCircleIconProps> = ({
  width = 24,
  height = 24,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="#D97706"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="12"
      y1="8"
      x2="12"
      y2="12"
      stroke="#D97706"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="16" r="1" fill="#D97706" />
  </svg>
);

// ─── Props Interface ──────────────────────────────────────────────────────────

interface MicrophoneAccessWarningModalProps {
  show: boolean;
  onClose: () => void;
}

// ─── Browser-Specific Instructions ───────────────────────────────────────────

interface MicInstructionStep {
  key: string;
  content: React.ReactNode;
}

function getBrowserMicInstructions(): {
  heading: string;
  steps: MicInstructionStep[];
} {
  const browser = detectBrowser();

  switch (browser) {
    case Browser.Chrome:
    case Browser.Edge:
    case Browser.Opera:
      return {
        heading: `Microphone access required ${browser ? `— ${browser}` : ''}.`,
        steps: [
          {
            key: 'chrome-1',
            content: 'Click the lock/tune icon in the address bar.',
          },
          {
            key: 'chrome-2',
            content: (
              <>
                Find <strong>Microphone</strong> in the site settings.
              </>
            ),
          },
          {
            key: 'chrome-3',
            content: (
              <>
                Change the permission to <strong>Allow</strong>.
              </>
            ),
          },
          {
            key: 'chrome-4',
            content: <strong>Reload the page.</strong>,
          },
        ],
      };

    case Browser.Safari:
      return {
        heading: 'Microphone access required — Safari.',
        steps: [
          {
            key: 'safari-1',
            content: (
              <>
                Click <strong>Safari</strong> in the menu bar and select{' '}
                <strong>Settings</strong>.
              </>
            ),
          },
          {
            key: 'safari-2',
            content: (
              <>
                Go to the <strong>Websites</strong> tab and select{' '}
                <strong>Microphone</strong>.
              </>
            ),
          },
          {
            key: 'safari-3',
            content: (
              <>
                Find this site and set it to <strong>Allow</strong>.
              </>
            ),
          },
          {
            key: 'safari-4',
            content: <strong>Reload the page.</strong>,
          },
        ],
      };
    case Browser.Firefox:
      return {
        heading: 'Microphone access required — Firefox.',
        steps: [
          {
            key: 'firefox-1',
            content: 'Click the lock icon in the address bar.',
          },
          {
            key: 'firefox-2',
            content: (
              <>
                Click <strong>Connection secure</strong> →{' '}
                <strong>More information</strong>.
              </>
            ),
          },
          {
            key: 'firefox-3',
            content: (
              <>
                Go to <strong>Permissions</strong> tab and uncheck "Use Default"
                for Microphone, then select <strong>Allow</strong>.
              </>
            ),
          },
          {
            key: 'firefox-4',
            content: <strong>Reload the page.</strong>,
          },
        ],
      };
    default:
      return {
        heading: 'Microphone access required.',
        steps: [
          {
            key: 'default-1',
            content: "Click the lock icon next to your browser's address bar.",
          },
          {
            key: 'default-2',
            content: 'Open Site settings or Permissions.',
          },
          {
            key: 'default-3',
            content: (
              <>
                Find Microphone and select <strong>Allow</strong>.
              </>
            ),
          },
          {
            key: 'default-4',
            content: (
              <>
                <strong>Refresh this page</strong> to apply changes.
              </>
            ),
          },
        ],
      };
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

const MicrophoneAccessWarningModal: React.FC<MicrophoneAccessWarningModalProps> = ({
  show,
  onClose,
}) => {
  const { heading, steps } = useMemo(() => getBrowserMicInstructions(), []);

  return (
    <Modal
      show={show}
      onClose={onClose}
      onHide={onClose}
      backdrop="static"
      showCloseIcon
      className="mic-access-warning-modal"
      hideFooter
    >
      {/* Body */}
      <div className="mic-access-warning-modal-body">
        {/* Heading row */}
        <div className="mic-access-warning-modal-heading-row">
          <AlertCircleIcon />
          <h2 className="mic-access-warning-modal-title">{heading}</h2>
        </div>

        {/* Description */}
        <p className="mic-access-warning-modal-description">
          We need permission to access your microphone so you can make calls and
          receive calls.
        </p>

        {/* Steps box */}
        <div className="mic-access-warning-modal-steps-box">
          <p className="mic-access-warning-modal-steps-heading">
            Steps to allow access
          </p>
          <ol className="mic-access-warning-modal-steps-list">
            {steps.map((step) => (
              <li key={step.key}>{step.content}</li>
            ))}
          </ol>
        </div>
      </div>

      {/* Footer */}
      <div className="mic-access-warning-modal-footer">
        <Button variant="primary" onClick={onClose}>
          Got it
        </Button>
      </div>
    </Modal>
  );
};

export default MicrophoneAccessWarningModal;
